import {faHeart} from "@fortawesome/free-regular-svg-icons";
import {faCartShopping, faGlobe, faMagnifyingGlass, faXmark, faSpinner} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useState, useEffect, useRef} from "react";
import {Link, useNavigate} from "react-router-dom";
import {UserOutlined} from "@ant-design/icons";

import logo from "../../assets/Logo2.png";
import {language} from "../../language/Language";
import {Avatar, Dropdown} from "antd";
// استيراد الدوال من localStorage.js
import {
    getCurrentUser,
    setCurrentUser,
    logout,
    getCartItemsCount,
    getFavoritesCount,
    transferGuestDataToUser,
} from "../../utils/localStorage";
import {useSearchProductsQuery, useGetRewardPointsQuery} from "../../RTK/Slices/Slices/apiSliceMeat";
import {useHasTicker} from "../TickerBar";

const Navbar = () => {
    const hasTicker = useHasTicker();
    // Search states
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const searchInputRef = useRef(null);
    const searchResultsRef = useRef(null);

    // Debounce search term to avoid too many API calls
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // Wait 500ms after user stops typing

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // API call with debounced search term
    const {data, isLoading} = useSearchProductsQuery(debouncedSearchTerm, {
        skip: !debouncedSearchTerm.trim(), // Skip API call if search term is empty
    });

    console.log("useSearchProductsQuery", data);

    // State للمستخدم وعدادات السلة والمفضلة
    const [currentUser, setCurrentUserState] = useState(null);
    const {data: navRewardPointsData} = useGetRewardPointsQuery(currentUser?.contact_id, {
        skip: !currentUser?.contact_id,
    });
    const [cartCount, setCartCount] = useState(0);
    const [favoritesCount, setFavoritesCountState] = useState(0);

    // State for mobile menu
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeLink, setActiveLink] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        const handleOpenMenu = () => {
            setIsMenuOpen(true);
            document.body.style.overflow = "hidden"; // منع التمرير
        };

        window.addEventListener("openMenu", handleOpenMenu);

        return () => {
            window.removeEventListener("openMenu", handleOpenMenu);
        };
    }, []);

    // Check if RTL direction
    const isRTL = language === "ar";

    // تحديث العدادات
    const updateCounts = () => {
        setCartCount(getCartItemsCount());
        setFavoritesCountState(getFavoritesCount());
    };

    // تحديث بيانات المستخدم
    const updateUserData = () => {
        try {
            const userStr = localStorage.getItem("user");
            const userData = userStr ? JSON.parse(userStr) : null;

            if (userData && userData.contact_id) {
                // إذا كان المستخدم مسجل دخول
                const currentUserId = getCurrentUser();

                // إذا لم يكن هناك مستخدم حالي أو تغير المستخدم
                if (currentUserId !== userData.contact_id) {
                    // نقل بيانات الضيف إلى المستخدم المسجل
                    transferGuestDataToUser(userData.contact_id);
                } else {
                    // تعيين المستخدم الحالي
                    setCurrentUser(userData.contact_id);
                }

                setCurrentUserState(userData);
            } else {
                // إذا لم يكن هناك مستخدم مسجل
                setCurrentUser(null);
                setCurrentUserState(null);
            }
        } catch (err) {
            console.error("فشل في قراءة بيانات المستخدم:", err);
            setCurrentUser(null);
            setCurrentUserState(null);
        }
    };

    useEffect(() => {
        // تحديث بيانات المستخدم والعدادات عند تحميل المكون
        updateUserData();
        updateCounts();

        // Retrieve active link from sessionStorage
        const savedActiveLink = sessionStorage.getItem("activeLink");
        if (savedActiveLink) {
            setActiveLink(savedActiveLink);
        } else {
            setActiveLink("New"); // Default state
        }

        // إضافة مستمعين للأحداث المخصصة
        const handleCartUpdate = () => updateCounts();
        const handleFavoritesUpdate = () => updateCounts();
        const handleStorageChange = (e) => {
            if (e.key === "user") {
                updateUserData();
            }
        };

        window.addEventListener("cartUpdated", handleCartUpdate);
        window.addEventListener("favoritesUpdated", handleFavoritesUpdate);
        window.addEventListener("storage", handleStorageChange);

        // Reset body overflow when component unmounts
        return () => {
            document.body.style.overflow = "auto";
            window.removeEventListener("cartUpdated", handleCartUpdate);
            window.removeEventListener("favoritesUpdated", handleFavoritesUpdate);
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    // مراقبة تغييرات localStorage للمستخدم
    useEffect(() => {
        const interval = setInterval(() => {
            updateUserData();
            updateCounts();
        }, 1000); // فحص كل ثانية

        return () => clearInterval(interval);
    }, []);

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchResultsRef.current &&
                !searchResultsRef.current.contains(event.target) &&
                !searchInputRef.current.contains(event.target)
            ) {
                setShowResults(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Close menu when route changes or when pressing escape key
    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === "Escape") {
                if (isSearchOpen) {
                    handleCloseSearch();
                } else if (isMenuOpen) {
                    handleToggleMenu();
                }
            }
        };

        window.addEventListener("keydown", handleEscapeKey);

        return () => {
            window.removeEventListener("keydown", handleEscapeKey);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMenuOpen, isSearchOpen]);

    // Show results when search data is available
    useEffect(() => {
        if (debouncedSearchTerm && (data?.data?.length > 0 || !isLoading)) {
            setShowResults(true);
        }
    }, [data, debouncedSearchTerm, isLoading]);

    const handleToggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);

        // Add body overflow control to prevent scrolling when menu is open
        if (!isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
    };

    const handleSetActiveLink = (link) => {
        setActiveLink(link);
        sessionStorage.setItem("activeLink", link); // Store active link in sessionStorage
        setIsMenuOpen(false); // Close the menu when a link is clicked
    };

    // Search handlers
    const handleSearchClick = () => {
        setIsSearchOpen(true);
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 100);
    };

    const handleCloseSearch = () => {
        setIsSearchOpen(false);
        setSearchTerm("");
        setDebouncedSearchTerm("");
        setShowResults(false);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (!value.trim()) {
            setShowResults(false);
        }
    };

    const handleProductClick = (product) => {
        // Navigate to product details page or handle product selection
        console.log("Selected product:", product);
        setShowResults(false);
        setIsSearchOpen(false);
        setSearchTerm("");
        // Example: navigate to product page
        navigate(`/Products/${product.product_id}`);
    };

    // دالة تسجيل الخروج
    const handleLogout = () => {
        logout(); // استدعاء دالة الخروج من localStorage.js
        localStorage.removeItem("user"); // إزالة بيانات المستخدم
        setCurrentUserState(null);
        updateCounts(); // تحديث العدادات
        // navigate("/");
    };

    const languageItems = [
        {
            key: "1",
            label: (
                <div
                    className={`${isRTL ? "hover:-translate-x-3" : "hover:translate-x-3"} transition-transform`}
                    onClick={(e) => {
                        e.preventDefault();
                        // تعيين اللغة والاتجاه
                        localStorage.setItem("language", "ar");
                        document.documentElement.lang = "ar";
                        document.documentElement.dir = "rtl";
                        // إضافة فئة rtl للجسم إذا لزم الأمر
                        document.body.classList.add("rtl");
                        document.body.classList.remove("ltr");
                        // تأخير إعادة التحميل قليلاً للسماح بتطبيق التغييرات
                        setTimeout(() => {
                            window.location.reload();
                        }, 100);
                    }}
                    style={{color: isRTL ? "#3c87c5" : "black"}}
                >
                    {isRTL ? "عربي" : "ar"}
                </div>
            ),
        },
        {
            key: "2",
            label: (
                <div
                    className={`${isRTL ? "hover:-translate-x-3" : "hover:translate-x-3"} transition-transform`}
                    onClick={(e) => {
                        e.preventDefault();
                        // تعيين اللغة والاتجاه
                        localStorage.setItem("language", "en");
                        document.documentElement.lang = "en";
                        document.documentElement.dir = "ltr";
                        // إضافة فئة ltr للجسم إذا لزم الأمر
                        document.body.classList.add("ltr");
                        document.body.classList.remove("rtl");
                        // تأخير إعادة التحميل قليلاً للسماح بتطبيق التغييرات
                        setTimeout(() => {
                            window.location.reload();
                        }, 100);
                    }}
                    style={{color: isRTL ? "black" : "#3c87c5"}}
                >
                    {isRTL ? "انجليزي" : "en"}
                </div>
            ),
        },
    ];

    // User dropdown items
    const userItems = [
        ...(navRewardPointsData?.enabled
            ? [
                  {
                      key: "points",
                      label: (
                          <div className="text-sm text-mainColor font-semibold py-1">
                              {isRTL
                                  ? `نقاطي: ${navRewardPointsData?.current_points || 0} ${navRewardPointsData?.points_name || "نقطة"}`
                                  : `My points: ${navRewardPointsData?.current_points || 0}`}
                          </div>
                      ),
                  },
              ]
            : []),
        {
            key: "1",
            label: (
                <div
                    className={`${isRTL ? "hover:-translate-x-3" : "hover:translate-x-3"} transition-transform`}
                    onClick={(e) => {
                        e.preventDefault();
                        handleLogout();
                    }}
                >
                    {isRTL ? "تسجيل الخروج" : "Sign out"}
                </div>
            ),
        },
        {
            key: "2",
            label: (
                <Link
                    to={"/ProfileUser"}
                    className={`block ${isRTL ? "hover:-translate-x-3" : "hover:translate-x-3"} transition-transform`}
                >
                    {isRTL ? "ملف الشخصي" : "Profile"}
                </Link>
            ),
        },
        {
            key: "3",
            label: (
                <Link
                    to={"/MyOrder"}
                    className={`block ${isRTL ? "hover:-translate-x-3" : "hover:translate-x-3"} transition-transform`}
                >
                    {isRTL ? "طلباتي" : "My Orders"}
                </Link>
            ),
        },
    ];

    // Navigation links
    const navLinks = [
        {
            id: "Home",
            path: "/",
            label: isRTL ? "الرئيسية" : "Home",
        },
        {
            id: "Products",
            path: "/Products",
            label: isRTL ? "المنتجات" : "Products",
        },
        {
            id: "FeaturedProducts",
            path: "/FeaturedProducts",
            label: isRTL ? "المنتجات الاكثر مبيعا" : "Best selling products",
        },
        {
            id: "Brands",
            path: "/Brands",
            label: isRTL ? "الشركات (كل الشركات)" : "Companies (All companies)",
        },
    ];

    return (
        <>
            <header
                className="w-full shadow-md fixed left-0 mx-auto flex border-b font-[sans-serif] min-h-[60px] md:min-h-[80px] tracking-wide z-50"
                style={{
                    backgroundColor: "var(--header-bg-color, #ffffff)",
                    color: "var(--header-text-color, inherit)",
                    top: hasTicker ? 42 : 0,
                    transition: "top 0.3s",
                }}
            >
                <div className="container flex flex-nowrap items-center lg:gap-y-2 gap-3 sm:gap-4 w-full">
                    <Link to="/">
                        <img src={logo} alt="logo" className="w-20 md:w-36" />
                    </Link>

                    {/* شريط البحث المدمج في الهيدر — موبايل فقط */}
                    <div className="flex-1 md:hidden mx-2">
                        <div className="relative">
                            <FontAwesomeIcon
                                icon={faMagnifyingGlass}
                                className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? "right-3" : "left-3"} text-gray-400 text-sm`}
                            />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onFocus={() => setIsSearchOpen(true)}
                                placeholder={isRTL ? "ابحث..." : "Search..."}
                                className={`w-full ${isRTL ? "pr-9 pl-3" : "pl-9 pr-3"} py-2 rounded-full text-sm text-gray-800 bg-white bg-opacity-90 border-0 outline-none focus:ring-2 focus:ring-white focus:ring-opacity-60`}
                                style={{fontFamily: "Cairo, sans-serif"}}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div
                        className={`lg:${isRTL ? "mr-10" : "ml-10"} xl:block 
            ${isMenuOpen ? "block" : "lg:block hidden"} 
            max-lg:before:fixed max-lg:before:bg-black max-lg:before:opacity-50 max-lg:before:inset-0 max-lg:before:z-50
            ${isMenuOpen ? "max-lg:before:block" : "max-lg:before:hidden"}`}
                    >
                        <button
                            onClick={handleToggleMenu}
                            className={`lg:hidden fixed w-12 ${
                                isRTL ? "left-4" : "right-4"
                            } z-[100] rounded-full bg-white p-3 shadow-lg`}
                            style={{top: hasTicker ? 54 : 12, transition: "top 0.3s"}}
                        >
                            <FontAwesomeIcon icon={faXmark} size="xl" style={{color: "#333"}} />
                        </button>

                        <ul
                            className={`lg:flex lg:gap-x-3 max-lg:space-y-3 max-lg:fixed max-lg:bg-white max-lg:w-1/2 max-lg:min-w-[300px] max-lg:top-0 ${
                                isRTL ? "max-lg:right-0" : "max-lg:left-0"
                            } max-lg:p-6 max-lg:h-full max-lg:shadow-md max-lg:overflow-auto max-lg:transition-transform max-lg:duration-300 ${
                                isMenuOpen
                                    ? "max-lg:translate-x-0"
                                    : isRTL
                                    ? "max-lg:translate-x-full"
                                    : "max-lg:-translate-x-full"
                            } z-50`}
                        >
                            {navLinks.map((link) => (
                                <li key={link.id} className="max-lg:border-b max-lg:py-3 px-2">
                                    <Link
                                        to={link.path}
                                        className={`${
                                            activeLink === link.id ? "text-mainColor font-bold" : "text-[#333]"
                                        } hover:text-mainColor text-[15px] block font-semibold`}
                                        // onClick={() => handleSetActiveLink(link.id)}
                                        onClick={() => {
                                            handleSetActiveLink(link.id);
                                            document.body.style.overflow = "auto"; // رجع السكرول
                                        }}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={`flex gap-x-6 gap-y-4 ${isRTL ? "mr-auto" : "ml-auto"}`}>
                        <div className="flex items-center gap-4 md:gap-5">
                            <Dropdown
                                className="cursor-pointer hidden md:inline-flex"
                                menu={{items: languageItems}}
                                trigger={["click"]}
                                placement={isRTL ? "bottomRight" : "bottomLeft"}
                                arrow={{pointAtCenter: true}}
                                dropdownRender={(menu) => <div style={{width: "150px"}}>{menu}</div>}
                            >
                                <FontAwesomeIcon className="text-md md:!text-xl" icon={faGlobe} />
                            </Dropdown>

                            <Link to={"Favorite"} className="relative hidden md:inline-block">
                                <FontAwesomeIcon icon={faHeart} className="text-md md:!text-xl" />
                                <span
                                    className={`absolute ${
                                        isRTL ? "right-auto -mr-1" : "left-auto -ml-1"
                                    } top-0 rounded-full bg-red-500 px-1 py-0 text-xs text-white`}
                                >
                                    {favoritesCount}
                                </span>
                            </Link>

                            <Link to={"Cart"} className="relative hidden md:inline-block">
                                <FontAwesomeIcon icon={faCartShopping} className="text-md md:!text-xl" />
                                <span
                                    className={`absolute ${
                                        isRTL ? "right-auto -mr-1" : "left-auto -ml-1"
                                    } top-0 rounded-full bg-red-500 px-1 py-0 text-xs text-white`}
                                >
                                    {cartCount}
                                </span>
                            </Link>

                            <button onClick={handleSearchClick} className="cursor-pointer hidden md:inline-block" style={{color: "var(--header-text-color, #333333)"}}>
                                <FontAwesomeIcon icon={faMagnifyingGlass} className="text-md md:!text-xl" />
                            </button>

                            {currentUser ? (
                                <Dropdown
                                    className="cursor-pointer"
                                    menu={{items: userItems}}
                                    trigger={["click"]}
                                    placement={isRTL ? "bottomRight" : "bottomLeft"}
                                    arrow={{pointAtCenter: true}}
                                    dropdownRender={(menu) => <div style={{width: "150px"}}>{menu}</div>}
                                >
                                    <Avatar className="bg-mainColor !w-6 !h-6 md:!w-8 md:!h-8 text-sm md:text-base" icon={<UserOutlined />} />
                                </Dropdown>
                            ) : (
                                <Link
                                    to={"Login"}
                                    className="px-3 !text-[10px] md:!text-[14px] md:px-5 py-1 md:py-2 text-sm rounded-full text-white border-2 border-mainColor bg-mainColor hover:bg-HovMainColor"
                                >
                                    {isRTL ? "التسجيل" : "Sign In"}
                                </Link>
                            )}

                            <button onClick={handleToggleMenu} className="lg:hidden">
                                <svg
                                    className="w-7 h-7"
                                    fill="var(--header-text-color, #ffffff)"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* شريط بحث الموبايل القديم - مخفي لأن البحث بقى مدمج في الهيدر */}
            <div className="hidden">
                <div className="relative">
                    <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        className={`absolute top-1/2 transform -translate-y-1/2 ${
                            isRTL ? "right-3" : "left-3"
                        } text-gray-400`}
                    />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onFocus={() => setIsSearchOpen(true)}
                        placeholder={isRTL ? "ابحث عن المنتجات..." : "Search for products..."}
                        className={`w-full ${
                            isRTL ? "pr-10 pl-4" : "pl-10 pr-4"
                        } py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-mainColor text-sm`}
                        autoComplete="off"
                    />
                </div>
            </div>

            {/* Search Overlay */}
            {isSearchOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-start justify-center pt-20">
                    <div className="bg-white w-full max-w-2xl mx-4 rounded-lg shadow-lg relative">
                        {/* Search Header */}
                        <div className="flex items-center p-4 border-b">
                            <div className="flex-1 relative">
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlass}
                                    className={`absolute top-1/2 transform -translate-y-1/2 ${
                                        isRTL ? "right-3" : "left-3"
                                    } text-gray-400`}
                                />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    placeholder={isRTL ? "ابحث عن المنتجات..." : "Search for products..."}
                                    className={`w-full ${
                                        isRTL ? "pr-10 pl-4" : "pl-10 pr-4"
                                    } py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-mainColor`}
                                    autoComplete="off"
                                />
                                {isLoading && (
                                    <FontAwesomeIcon
                                        icon={faSpinner}
                                        className={`absolute top-1/2 transform -translate-y-1/2 ${
                                            isRTL ? "left-3" : "right-3"
                                        } text-mainColor animate-spin`}
                                    />
                                )}
                            </div>
                            <button
                                onClick={handleCloseSearch}
                                className={`${isRTL ? "mr-4" : "ml-4"} p-2 hover:bg-gray-100 rounded-full`}
                            >
                                <FontAwesomeIcon icon={faXmark} className="text-gray-500" />
                            </button>
                        </div>

                        {/* Search Results */}
                        {showResults && (
                            <div ref={searchResultsRef} className="max-h-96 overflow-y-auto">
                                {isLoading ? (
                                    <div className="p-8 text-center">
                                        <FontAwesomeIcon
                                            icon={faSpinner}
                                            className="text-mainColor text-2xl animate-spin mb-2"
                                        />
                                        <p className="text-gray-500">{isRTL ? "جاري البحث..." : "Searching..."}</p>
                                    </div>
                                ) : data?.data?.length > 0 ? (
                                    <div className="p-2">
                                        {data.data.map((product) => (
                                            <div
                                                key={product.product_id}
                                                onClick={() => handleProductClick(product)}
                                                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer rounded-lg transition-colors"
                                            >
                                                <img
                                                    src={product.image_url}
                                                    alt={product.name}
                                                    className="w-12 h-12 object-cover rounded-lg"
                                                    onError={(e) => {
                                                        e.target.src = "/placeholder-image.jpg"; // Add a placeholder image
                                                    }}
                                                />
                                                <div className={`flex-1 ${isRTL ? "mr-3" : "ml-3"}`}>
                                                    <h3 className="font-medium text-gray-900 line-clamp-1">
                                                        {product.name}
                                                    </h3>
                                                    {/* <p className="text-sm text-gray-500">
                            {product.cat_name}
                          </p> */}
                                                    <div className="flex items-center justify-between mt-1">
                                                        <span className="text-mainColor font-bold">
                                                            {product.price
                                                                ? `${parseFloat(product.price)} ${
                                                                      isRTL ? "ج.م" : "EGP"
                                                                  }`
                                                                : ""}
                                                            {/* {isRTL ? "ج.م" : "EGP"} */}
                                                        </span>
                                                        {/* <span className="text-xs text-gray-400">
                              {isRTL ? "متوفر:" : "Stock:"}{" "}
                              {parseFloat(product.stock)}
                            </span> */}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : debouncedSearchTerm ? (
                                    <div className="p-8 text-center">
                                        <p className="text-gray-500">
                                            {isRTL ? "لم يتم العثور على منتجات" : "No products found"}
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Navbar;
